"use client"

import { useState, useEffect } from "react"
import type { AgentDemoStep, MainToolCall, SubAgent } from "@/lib/agent-demo-data"
import type { ScenarioSkill } from "@/lib/demo-scenarios/scenario-skills"
import { SafeClawLogo } from "@/src/components/ui/safeclaw-logo"
import { AgentMessageBubble } from "./agent-message-bubble"
import {
  CHAT_EMPTY_GUIDES,
  WELCOME_TAGLINE_INTERVAL_MS,
} from "@/lib/welcome-taglines"

/* ── Empty state: 大 Logo 居中 + 引导开始对话 ── */
function EmptyStateWelcome() {
  const [guideIndex, setGuideIndex] = useState(0)
  useEffect(() => {
    const t = setInterval(() => {
      setGuideIndex((i) => (i + 1) % CHAT_EMPTY_GUIDES.length)
    }, WELCOME_TAGLINE_INTERVAL_MS)
    return () => clearInterval(t)
  }, [])
  return (
    <div className="flex w-full max-w-2xl mx-auto flex-col items-center justify-center gap-8 animate-in fade-in duration-500 px-4 py-8">
      <SafeClawLogo
        height={112}
        className="max-w-[min(92vw,520px)] w-auto shrink-0 opacity-95"
      />
      <div className="w-full max-w-xl flex justify-center px-2">
        <p
          key={guideIndex}
          className="text-sm text-muted-foreground text-center leading-relaxed animate-in fade-in duration-500 max-w-prose"
        >
          {CHAT_EMPTY_GUIDES[guideIndex]}
        </p>
      </div>
    </div>
  )
}

interface AgentMessageListProps {
  visibleSteps: AgentDemoStep[]
  showInput: boolean
  mainToolsState: (MainToolCall & { status: "running" | "done" })[]
  liveSubAgents: SubAgent[]
  allSubAgentsDone: boolean
  selectedAgentId: string | null
  onAgentClick: (agentId: string) => void
  roleIcons: Record<string, React.ReactNode>
  scenarioSkills: ScenarioSkill[]
  onSkillsLoaded: () => void
  onPublish: () => void
  scrollRef: React.Ref<HTMLDivElement>
  onScroll: () => void
}

export function AgentMessageList({
  visibleSteps,
  showInput,
  mainToolsState,
  liveSubAgents,
  allSubAgentsDone,
  selectedAgentId,
  onAgentClick,
  roleIcons,
  scenarioSkills,
  onSkillsLoaded,
  onPublish,
  scrollRef,
  onScroll,
}: AgentMessageListProps) {
  return (
    <div
      ref={scrollRef}
      onScroll={onScroll}
      className="flex min-h-0 flex-1 flex-col overflow-y-auto no-scrollbar"
    >
      {showInput && visibleSteps.length === 0 ? (
        /* 空状态：独占滚动区并垂直水平居中，避免贴在顶部 */
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-8">
          <EmptyStateWelcome />
        </div>
      ) : (
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-4 px-4 pb-24 pt-20 lg:px-0">
          {visibleSteps.map((step, idx) => (
            <AgentMessageBubble
              key={idx}
              step={step}
              idx={idx}
              allSteps={visibleSteps}
              mainToolsState={mainToolsState}
              liveSubAgents={liveSubAgents}
              allSubAgentsDone={allSubAgentsDone}
              selectedAgentId={selectedAgentId}
              onAgentClick={onAgentClick}
              roleIcons={roleIcons}
              scenarioSkills={scenarioSkills}
              onSkillsLoaded={onSkillsLoaded}
              onPublish={onPublish}
            />
          ))}
          <div className="h-4" />
        </div>
      )}
    </div>
  )
}
